(() => {
  if (customElements.get('cf-search-results')) return;
  class CFSearchResults extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.events = new AbortController();
      this.collectionState = 'idle';
      const options = { signal: this.events.signal };
      this.querySelector('[data-collection-tab]')?.addEventListener('click', event => {
        event.preventDefault();
        const target = this.querySelector('[data-collections]');
        history.pushState(null, '', `#${target.id}`);
        this.updateView(true);
        target.focus({ preventScroll: true });
      }, options);
      this.querySelector('[data-retry]')?.addEventListener('click', () => this.loadCollections(), options);
      window.addEventListener('popstate', () => this.updateView(), options);
      window.addEventListener('hashchange', () => this.updateView(), options);
      document.addEventListener('shopify:block:select', event => {
        const card = this.querySelector(`[data-shopify-editor-block*="${CSS.escape(event.detail.blockId)}"]`);
        card?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }, options);
      this.querySelector('[data-clear-search]')?.addEventListener('click', () => {
        const input = this.querySelector('input[name="q"]');
        if (input) { input.value = ''; input.focus(); }
      }, options);
      this.setupToolbar(options);
      this.setupRecent(options);
      this.updateView();
    }
    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      this.connected = false;
    }
    updateView(forceCollections = false) {
      const panel = this.querySelector('[data-collections]');
      if (!panel) return;
      const onlyCollections = forceCollections || location.hash === `#${panel.id}`;
      this.querySelectorAll('[data-native-results]').forEach(el => { el.hidden = onlyCollections; });
      panel.hidden = !onlyCollections && this.dataset.activeType !== 'all';
      const tab = this.querySelector('[data-collection-tab]');
      if (onlyCollections) tab.setAttribute('aria-current', 'page');
      else tab.removeAttribute('aria-current');
      this.querySelectorAll('[data-native-tab]').forEach(link => {
        const type = new URL(link.href).searchParams.get('type');
        const active = this.dataset.activeType === 'all' ? type === 'product,page,article' || type === 'product,page' : type === this.dataset.activeType;
        if (active && !onlyCollections) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      });
      if (!panel.hidden && this.collectionState === 'idle') this.loadCollections();
    }
    async loadCollections() {
      this.request?.abort();
      const controller = new AbortController();
      this.request = controller;
      this.collectionState = 'loading';
      const status = this.querySelector('[data-collection-status]');
      const retry = this.querySelector('[data-retry]');
      const list = this.querySelector('[data-collection-list]');
      status.textContent = 'Loading related collections…';
      retry.hidden = true;
      list.replaceChildren();
      const url = new URL(this.dataset.suggestUrl, location.origin);
      url.searchParams.set('q', this.dataset.term);
      url.searchParams.set('resources[type]', 'collection');
      url.searchParams.set('resources[limit]', '10');
      const timeout = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Search unavailable');
        const data = await response.json();
        if (!this.isConnected || this.request !== controller) return;
        const collections = data.resources?.results?.collections;
        if (!Array.isArray(collections)) throw new Error('Invalid search response');
        const seen = new Set();
        for (const item of collections) {
          if (typeof item.title !== 'string' || typeof item.url !== 'string') continue;
          const destination = new URL(item.url, location.origin);
          if (destination.origin !== location.origin || !/^https?:$/.test(destination.protocol) || seen.has(destination.href)) continue;
          seen.add(destination.href);
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.className = 'cf-search__text-link';
          a.href = destination.href;
          const icon = document.createElement('span');
          icon.className = 'cf-search__document';
          icon.setAttribute('aria-hidden', 'true');
          const title = document.createElement('strong');
          title.textContent = item.title;
          const arrow = document.createElement('span');
          arrow.textContent = '→';
          arrow.setAttribute('aria-hidden', 'true');
          a.append(icon, title, arrow);
          li.append(a);
          list.append(li);
        }
        status.textContent = list.children.length ? '' : 'No related collections found.';
        this.collectionState = 'loaded';
      } catch (error) {
        if (!this.isConnected || this.request !== controller) return;
        this.collectionState = 'error';
        status.textContent = 'Collections could not be loaded. Please try again.';
        retry.hidden = false;
      } finally { clearTimeout(timeout); }
    }
    setupToolbar(options) {
      const form = this.querySelector('.cf-search__filters');
      if (!form) return;
      const filterPanel = form.querySelector('[data-filter-panel]');
      const sortPanel = form.querySelector('[data-sort-panel]');
      const media = window.matchMedia('(min-width: 750px)');
      const resize = () => {
        [filterPanel, sortPanel].filter(Boolean).forEach(panel => { panel.open = media.matches; });
      };
      resize();
      media.addEventListener('change', resize, options);
      if (filterPanel && !filterPanel.querySelector('.cf-search__filter')) filterPanel.hidden = true;
      form.querySelector('select[name="sort_by"]')?.addEventListener('change', () => form.requestSubmit(), options);
      const close = (returnFocus = false) => {
        form.querySelectorAll('.cf-search__filter[open]').forEach(item => { item.open = false; });
        if (!media.matches) {
          const active = [filterPanel, sortPanel].find(panel => panel?.open);
          [filterPanel, sortPanel].filter(Boolean).forEach(panel => { panel.open = false; });
          if (returnFocus) active?.querySelector('summary')?.focus();
        }
      };
      document.addEventListener('click', event => { if (!form.contains(event.target)) close(); }, options);
      form.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          const details = event.target.closest('.cf-search__filter');
          close(true);
          if (media.matches) details?.querySelector('summary')?.focus();
        }
      }, options);
      [filterPanel, sortPanel].filter(Boolean).forEach(panel => {
        panel.addEventListener('toggle', () => {
          if (panel.open && !media.matches) [filterPanel, sortPanel].filter(other => other && other !== panel).forEach(other => { other.open = false; });
        }, options);
      });
      form.querySelectorAll('.cf-search__filter').forEach(item => item.addEventListener('toggle', () => {
        if (item.open && media.matches) form.querySelectorAll('.cf-search__filter').forEach(other => { if (other !== item) other.open = false; });
      }, options));

    }
    setupRecent(options) {
      const container = this.querySelector('[data-recent]');
      if (!container) return;
      const key = 'cfRecentSearchesV1';
      const maximum = Math.max(3, Math.min(12, Number(this.dataset.recentMax) || 6));
      let terms = [];
      try {
        const stored = JSON.parse(localStorage.getItem(key) || '[]');
        if (Array.isArray(stored)) terms = stored.filter(value => typeof value === 'string' && value.trim()).slice(0, maximum);
      } catch (_) { /* Storage may be unavailable. Search remains functional. */ }
      const current = (this.dataset.term || '').trim();
      if (current) terms = [current, ...terms.filter(value => value.toLowerCase() !== current.toLowerCase())].slice(0, maximum);
      const persist = () => { try { localStorage.setItem(key, JSON.stringify(terms)); } catch (_) {} };
      const render = () => {
        const list = container.querySelector('[data-recent-list]');
        list.replaceChildren();
        container.hidden = !terms.length;
        terms.forEach(term => {
          const li = document.createElement('li');
          const a = document.createElement('a');
          const url = new URL(this.dataset.searchUrl, location.origin);
          url.searchParams.set('q', term);
          url.searchParams.set('type', this.dataset.allTypes || 'product,page,article');
          url.searchParams.set('options[prefix]', 'last');
          a.href = url.href;
          a.textContent = term;
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.textContent = '×';
          remove.setAttribute('aria-label', `Remove recent search: ${term}`);
          remove.addEventListener('click', () => {
            terms = terms.filter(value => value !== term);
            persist(); render();
            container.querySelector('a, button')?.focus();
          }, { once: true });
          li.append(a, remove);
          list.append(li);
        });
      };
      container.querySelector('[data-clear-recent]').addEventListener('click', () => {
        terms = []; persist(); render();
        this.querySelector('input[name=q]')?.focus();
      }, options);
      persist(); render();
    }
  }
  customElements.define('cf-search-results', CFSearchResults);
})();